import {action, computed, observable, runInAction} from 'mobx';
import isEmpty from 'lodash/isEmpty';
import {getId, getNotificationSoundConfig, getOwnerDetails, isOwner, isStudent} from '../utils/userUtility';
import {CHAT, NOTIFICATION} from '../constants/urlConstants';
import request from '../utils/request';
import realtimeStore from '../stores/RealtimeStore';
import newMessageMP3 from '../assets/audio/new-message.mp3';
import {
    actions,
    events,
    LessonNotificationType,
    ModuleNotificationType,
    NotificationCategoryType,
    NotificationSoundStatus,
    NotificationType,
    PlatformMode,
    ProjectNotificationType,
    UserNotificationType
} from '../constants/enums';
import trackStore from '../containers/main/track/store';
import projectStore from '../containers/main/project/store';
import mainStore from '../containers/main/store';
import {go} from '../utils/navigationUtility';
import {customEvent} from '../utils/analyticsUtil';
import userStore from './UserStore';
import chatStore from '../containers/common/chat/store';
import looperStore from '../containers/create/looper/store';
import wizardStore from '../containers/studio/store';
import whiteBoardStore from "../containers/studio/whiteboard/store";
import videoChatStore from "../containers/video-chat/store";
import {translate} from 'utils/localizationUtility';

class NotificationStore {
    @observable userNotifications = [];
    systemNotificationBtnRef = null;
    messageNotificationBtnRef = null;

    @observable userPresence = [];
    @observable searchStr = '';

    constructor() {
        // autorun(() => {
        //     console.log('PRESENCCE STATUS: ', JSON.stringify(this.userPresence));
        // })
        //
        setInterval(() => {
            runInAction(() => {
                this.userPresence.map(up => {
                    up.isOnline = up.timestamp && (Date.now() - up.timestamp) <= 1000 * 60 * 2
                })
            })
        }, 1000 * 60);
    }

    getUserStatus = (id) => {
        return this.userPresence.find(up => up.id === id) || {isOnline: false, url: 'none'}
    }

    isUserOnline = (id) => {
        return this.getUserStatus(id).isOnline;
    }

    handleNotificationItemClick = async (notification) => {
        let context = 'passive';
        if (notification.objectType === NotificationType.Project) {
            switch (notification.type) {
                case ProjectNotificationType.ChatMessage:
                    mainStore.openMainAppDrawer();
                    await projectStore.setActiveProject(notification.objectId || notification.id);
                    context = 'active';
                    break;
                case ProjectNotificationType.Save:
                    context = 'active';
                    await this.handleStudentLessonNotificationClick(notification);
                    break;
                case ProjectNotificationType.Share:
                    context = 'active';
                    go('/students/activity');
                    break;
                default:
            }
        }
        if (notification.objectType === NotificationType.Lesson) {
            context = 'active';
            await this.handleStudentAssignNotificationClick(notification);
        }
        if (notification.objectType === NotificationType.User) {
            switch (notification.type) {
                case UserNotificationType.StudentSignedUp:
                    context = 'active';
                    go(`/home/select/student/${notification.objectId}`);
                    break;
                default:
            }
        }
        if (notification.objectType === NotificationType.Video) {
            context = 'active';
            go(`/home/select/student/${notification.objectId}`);
        }

        customEvent(events.NotificationClick, {
            context,
            type: notification.type,
            objectType: notification.objectType,
        });
    };

    handleStudentLessonNotificationClick = async (notification) => {
        go(`/create/${notification.objectId}`, {}, true);
        // mainStore.setStudioDrawerState(StudioDrawerState.Info);
        // mainStore.openMainAppDrawer();
    };

    handleStudentAssignNotificationClick = async (notification) => {
        mainStore.updatePlatformMode(PlatformMode.View);

        let lesson = await projectStore.fetchProject(notification.objectId);
        if (lesson.userId === getId() || lesson.parentOwnerId === getId()) {
            go(`/create/${lesson.id}`, {}, true);
        } else {
            console.log('Creating new lesson from notification click');
            const newLessonParams = {parentProjectId: lesson.id, parentOwnerId: lesson.userId, userId: getId(), ...getOwnerDetails()};
            await projectStore.createNewStudentLessonFromExisting(lesson.id, newLessonParams, {action: actions.CreateNewLessonFromPractice}, true);
        }
    };

    reconnectHandler = () => {
        console.log('reconnected, fetching notifications');
        this.fetchNotifications();
    }

    @action subscribeToNotifications = async () => {
        try {
            realtimeStore.setReconnectHandler(this.reconnectHandler);
            await realtimeStore.subscribe(`general:${getId()}`, (channel, data) => {
                if (!isOwner(data.from.id)) {
                    const newNotificationSound = new Audio(newMessageMP3);
                    this.pushNotification(data);
                    try {
                        if (this.shouldWePlayNotificationSound(data.hide)) {
                            newNotificationSound.play();
                        }
                    } catch (err) {
                    }
                }
            });
            await realtimeStore.subscribe(`server:${getId()}`, (channel, payload) => {
                    switch (payload.msg) {
                        case 'extractAudioMetadata':
                            if (payload.fileId) {
                                trackStore.updateData(payload.fileId, payload.data);
                            }
                            break;
                        case 'rnmf':
                            trackStore.addRnmf(payload.fileId);
                            break;
                        default:
                            userStore.updateUserPaymentStatus();
                    }
                }
            );
            await realtimeStore.subscribe(`server`, (channel, payload) => {
                    switch (payload.msg) {
                        case 'refreshScreen':
                            mainStore.setNeedToRefresh(true);
                            break;
                        default:
                    }
                }
            );

            await realtimeStore.subscribe(`presence:${getId()}`, async (channel, payload) => {
                runInAction(() => {
                    let userIndex = this.userPresence.findIndex(up => up.id === payload.from);
                    let up = {id: payload.from, timestamp: Date.now(), url: payload.currentUrl, isOnline: true};
                    if (userIndex >= 0) {
                        this.userPresence[userIndex] = up;
                    } else {
                        this.userPresence.push(up)
                    }
                })
                if (payload.ping) {
                    await realtimeStore.send(`presence:${payload.from}`, {from: getId(), pong: true, currentUrl: window.location.href});
                }
            });
        } catch (err) {
            console.log('failed connecting to realtime notification service');
        }
    };

    shouldWePlayNotificationSound = (forceMute) => {
        // don't hide notification and not in studio
        if (!forceMute && mainStore.isHomeMode) {
            switch (getNotificationSoundConfig()) {
                case NotificationSoundStatus.Enabled:
                    return true;
                case NotificationSoundStatus.MuteDuringVideo:
                    return !videoChatStore.videoChatStarted;
                case NotificationSoundStatus.Mute:
                default:
                    return false;
            }
        }
        return false;
    }

    @action subscribeToProject = async (projectId) => {
        await realtimeStore.subscribe(`project:${projectId}`, async (channel, payload) => {
            await this.handleNewNotification(payload);
        });
    }

    @action unsubscribeToProject = async (projectId) => {
        await realtimeStore.unsubscribe(`project:${projectId}`);
    };

    @action sendPresencePing = async (toUser) => {
        await realtimeStore.send(`presence:${toUser}`, {from: getId(), ping: true, currentUrl: window.location.href});
    }

    /**
     * Turn the old notification object to the new one
     * @param notification
     * @param addCount
     */
    @action handleNewNotification = async (notification, addCount) => {
        switch (notification.objectType) {
            case NotificationType.Project:
                switch (notification.type) {
                    case ProjectNotificationType.ChatMessage:
                        projectStore.updateField(notification.objectId || notification.id, 'lastModified', Date.now());
                        break;
                    default:
                        projectStore.fetchProject(notification.objectId || notification.id, true);
                        break;
                }
                break;
            case NotificationType.Canvas:
                switch (notification.type) {
                    case LessonNotificationType.CanvasUpdate:
                        await whiteBoardStore.updateCanvas(notification.canvasPatch);
                        break;
                    default:
                }
                break;
            case NotificationType.Lesson:
                switch (notification.type) {
                    case LessonNotificationType.OpenLessonFirst:
                        projectStore.updateField(notification.objectId || notification.id, 'openLessonFirst');
                        await projectStore.onStudentEnrollment(notification.parentProjectId, notification.from, notification.objectId || notification.id);
                        break;
                    case LessonNotificationType.Delete:
                        projectStore.removeFromTimeline(notification.objectId);
                        break;
                    case LessonNotificationType.LessonUpdate:
                        await looperStore.updateProject(notification.project);
                        break;
                    case LessonNotificationType.Presenting:
                        await wizardStore.setPresented(notification.presentMode);
                        break;
                    case LessonNotificationType.LessonActionUpdate:
                        await looperStore.processActionUpdate(notification.action, notification.additionalInfo);
                        break;
                    case LessonNotificationType.LessonUpdateRequest:
                        if (wizardStore.presentMode) {
                            await wizardStore.updatePresented();
                            await looperStore.sendProjectUpdate();
                            await wizardStore.sendNavigationUpdate();
                        }
                        break;
                    case LessonNotificationType.Navigation:
                        await wizardStore.updateNavigation(notification.navigation);
                        break;
                    case LessonNotificationType.Chat:
                        await chatStore.onReceiveNewMessage(notification.objectId, notification);
                        break;
                    default:
                        if (isStudent()) {
                            await runInAction(async () => {
                                projectStore.isProjectsLoaded = false;
                                await projectStore.fetchProjects();
                            });
                        } else {
                            const lessonId = notification.objectId || notification.id;
                            // const module = projectStore.getModuleByLessonId(lessonId);
                            // console.log('module',module)
                            // const lesson = module?.lessons?.find(lesson=>lesson.id === lessonId || lesson.parentOwnerId === lessonId);
                            // console.log('lesson',lesson);
                            const lesson = await projectStore.fetchProject(lessonId, true);
                            if (lesson.userId !== getId() && lesson.groupId) {
                                projectStore.updateActivities(lessonId, lesson.userId, lesson);
                            }
                        }
                        break;
                }
                break;
            case NotificationType.TrackLooped:
                if (addCount) {
                    trackStore.updateField(notification.objectId || notification.id, 'addCount');
                }
                break;
            case NotificationType.Module:
                switch (notification.type) {
                    case ModuleNotificationType.NewModule:
                    case ModuleNotificationType.EditModule:
                    case ModuleNotificationType.DeleteModule:
                    case ModuleNotificationType.MoveModule:
                    default:
                        await runInAction(async () => {
                            projectStore.isProjectsLoaded = false;
                            await projectStore.fetchProjects();
                        });
                }
                break;
            default:
                break;
        }
        return notification;
    };

    /**
     * Get all user notifications.
     * @returns {Array.<*>}
     */
    @computed
    get notifications() {
        return this.userNotifications.slice().filter(n => !n.hide).sort(this.sortByTime);
    }

    /**
     * Get all user notifications
     * @returns {Array.<*>}
     */
    @computed
    get systemNotifications() {
        return this.notifications.slice().filter(notification => notification.category === NotificationCategoryType.System)
    }

    /**
     * Get all user notifications
     * @returns {Array.<*>}
     */
    @computed
    get messengerNotifications() {
        return this.notifications.filter(notification => notification.category === NotificationCategoryType.Messenger);
    }

    /**
     * Get all user unread notifications
     * @returns {Array.<*>}
     */
    @computed
    get unreadNotifications() {
        return this.processNotifications(this.notifications.filter(notification => !notification.isRead));
    }

    /**
     * Get user unread messenger notifications count
     * @returns {Number}
     */
    @computed
    get unreadMessengerNotificationsCount() {
        return this.messengerNotifications.filter(n => !n.isRead && n.userId !== n.fromId).length;
    }

    /**
     * Get user unread system notifications count
     * @returns {Number}
     */
    @computed
    get unreadSystemNotificationsCount() {
        return this.systemNotifications.filter(n => !n.isRead).length;
    }

    /**
     * Get user unread notifications count
     * @returns {Number}
     */
    @computed
    get unreadNotificationsCount() {
        return this.notifications.filter(n => !n.isRead).length;
    }

    /**
     * Push a new notification to notification array
     * @param notification
     */
    @action
    async pushNotification(notification) {
        // if (notification.category === NotificationCategoryType.Messenger) {
        //     await projectStore.fetchProject(notification.objectId || notification.id);
        // }
        const newNotification = await this.handleNewNotification(notification, true);
        runInAction(() => {
            this.userNotifications = this.processNotifications(this.userNotifications.slice().concat(newNotification));
        });
    }

    sortByTime(a, b) {
        return new Date(b.created).getTime() - new Date(a.created).getTime();
    }

    /**
     * Process the user notification reducing the accumulative items
     * @param notifications {Array} The notification array
     */
    @action
    processNotifications(notifications) {
        if (!notifications) {
            return [];
        }
        return notifications.sort(this.sortByTime).reduce((result, notification) => {
            // if (notification.hide) {
            //     return result;
            // }
            if (notification.category === NotificationCategoryType.System) {
                notification.unread = !notification.isRead ? 1 : 0;
                result.push(notification);
            } else {
                const existingNotification = result
                    .find(item => item.objectId === notification.objectId && item.objectType === notification.objectType
                        && item.category === notification.category);
                if (!isEmpty(existingNotification)) {
                    if (!notification.isRead && notification.fromId !== getId()) {
                        existingNotification.unread = notification.unread + 1;
                    } else {
                        existingNotification.othersCount += 1;
                    }
                } else {
                    notification.othersCount = 0;
                    if (!notification.isRead && notification.fromId !== getId()) {
                        notification.unread = notification.count || 1; // if the message in not read, the count is the number if
                        // unread items
                    } else {
                        notification.unread = 0;
                    }
                    result.push(notification);
                }
            }
            return result;
        }, []);
    }

    getUnreadNotificationsCount = (type) => {
        switch (type) {
            case NotificationCategoryType.System:
                return this.unreadSystemNotificationsCount;
            case NotificationCategoryType.Messenger:
                return this.unreadMessengerNotificationsCount;
            default:
                return 0;
        }
    };

    /**
     * mark all notifications as read
     */
    @action markAllAsRead = (type) => {
        const notifications = this.notifications.filter(n => n.category === type && n.unread > 0);
        notifications.forEach((n) => {
            n.isRead = true;
            n.unread = 0;
        });
        if (notifications.length > 0) {
            request(`${NOTIFICATION}`, {method: 'POST', body: {category: type}});
        }
    };

    /**
     * mark a singlenotifications as read
     */
    @action markNotificationAsRead = (notification) => {
        if (notification.unread > 0 && notification.category === NotificationCategoryType.System) {
            request(`${NOTIFICATION}/${notification.id}`, {method: 'POST', body: {}});
        }
        let existingNotification = this.notifications
            .find(item => item.objectId === notification.objectId && item.objectType === notification.objectType
                && item.category === notification.category)

        if (existingNotification) {
            existingNotification.unread = 0;
        }
    };

    /**
     * mark all project notifications as read
     */
    @action markAllProjectNotificationsAsRead = () => {
        let chatId = chatStore.activeChat;
        if (chatId && this.unreadNotificationsCount > 0) {
            let counter = 0;
            this.notifications.map((notification) => {
                if (notification.objectId === chatId) {
                    if (!notification.isRead) counter += 1;
                    notification.isRead = true;
                    notification.count = 0;
                }
                return notification;
            });
            if (counter) {
                request(`${CHAT}/${chatId}`, {method: 'PUT', body: {}});
            }
        }
    };

    /**
     * Fetch all notifications
     * @returns {Promise.<void>}
     */
    @action
    async fetchNotifications() {
        const notifications = await request(`${NOTIFICATION}`);
        if (!notifications?.length) {
            return;
        }
        runInAction(() => {
            this.userNotifications = this.processNotifications(notifications || []);
            if (isStudent() && this.unreadTeachersNotificationsCount) {
                // chatStore.setActiveChat(this.userNotifications[0].teacher.id);
                // mainStore.openMainAppDrawer();
            }
        });
    }

    setNotificationRef = (key, value) => {
        this[key] = value;
    };

    triggerSystemNotificationPopup = () => {
        if (this.systemNotificationBtnRef) this.systemNotificationBtnRef.click();
    };

    triggerMessageNotificationPopup = () => {
        if (this.messageNotificationBtnRef) this.messageNotificationBtnRef.click();
    };

    getUnreadProjectMessages = (id) => {
        return this.messengerNotifications.filter(n => !n.isRead).reduce((res, item) => {
            if (item.objectId === id) {
                res.push(item);
            }
            return res;
        }, []);
    }

    @computed
    get sortedSystemNotifications() {
        const sortedNotifications = [...this.systemNotifications].sort(this.sortByTime);

        return sortedNotifications.filter(notification => {
                return `${notification.from.name}`.toLowerCase().includes(this.searchStr);
        });
    };

    @computed
    get sortedMessageNotifications() {
        const sortedNotifications = [...this.messengerNotifications].sort(this.sortByTime)
        return sortedNotifications.filter(notification => `${notification.student.name}`.toLowerCase().includes(this.searchStr))
    }


    getStudentNotification(student) {
        let notification = this.sortedMessageNotifications.find(n => n.student.id === student.id)
        return notification || {
            category: NotificationCategoryType.Messenger,
            count: 0,
            message: translate('messenger.chat.empty'),
            student: student,
            studentOrGroupId: student.id,
            teacher: getOwnerDetails().owner,
            userId: getId()
        }
    }

    getTeacherNotification(teacher) {
        let notification = this.sortedMessageNotifications.find(n =>
            n.teacher.id === teacher.id)
        return notification || {
            category: NotificationCategoryType.Messenger,
            count: 0,
            message: translate('messenger.chat.empty'),
            student: getOwnerDetails().owner,
            studentOrGroupId: getId(),
            teacher: teacher,
            userId: getId()
        }
    }

    @action setSearchSrt = (val) => {
        this.searchStr = (val || '').toLowerCase();
    };

    @computed
    get unreadTeachersNotificationsCount() {
        let res = this.notifications.reduce((res, n) => {
            res += n.unread || 0;
            return res;
        }, 0);

        return res;
    }
}

const notificationStore = new NotificationStore();
export default notificationStore;
